import { PublicTransitPresetEntity } from '../entities/replancity_PublicTransitPreset';
import restClient, { ApiError } from '../RestClient';
import {
    CopyPresetFnArgs,
    CreationPreset,
    PresetEntityName,
    ProjectPresets,
    TraceLineToNetworkArgsType,
    TraceRoutesToNetworkArgsType,
    TraceRouteToNetworkArgsType,
    SavePresetError,
    CopyScenarioEventArgsType,
} from './types';
import { FormDataObj } from '@utils/types';
import type * as schema from '../schema';

export const presetsApi = {
    getPresetsByEntityName: function ({
        projectId,
        presetEntityName,
        abortSignal,
    }: {
        projectId: string;
        presetEntityName: PresetEntityName;
        abortSignal: AbortSignal;
    }): Promise<schema.definitions['entities_replancity_RoadInfrastructurePreset'][]> {
        return restClient.fetch({
            method: 'GET',
            path: `queries/${presetEntityName}/preset?projectId=${projectId}`,
            fetchOptions: {
                signal: abortSignal,
                params: { view: '_local' },
            },
        });
    },

    getProjectPresets: function ({
        projectId,
        abortSignal,
    }: {
        projectId: string;
        abortSignal: AbortSignal;
    }): Promise<ProjectPresets> {
        return restClient.fetch({
            method: 'GET',
            path: `services/replancity_ProjectApiService/getAllPresets`,
            data: { projectId },
            fetchOptions: {
                signal: abortSignal,
            },
        });
    },

    getPresetsTypes: function ({
        abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<CreationPreset[]> {
        return restClient.fetch({
            method: 'GET',
            path: `presets`,
            fetchOptions: {
                signal: abortSignal,
                params: { view: '_local' },
            },
        });
    },

    copyPresetDistrictsToPreset: function ({
        abortSignal,
        ...data
    }: schema.paths['/services/replancity_RoadInfrastructureService/copyDistricts']['get']['parameters']['query'] & {
        abortSignal: AbortSignal;
    }): Promise<CreationPreset[]> {
        return restClient.fetch({
            method: 'GET',
            path: `services/replancity_RoadInfrastructureService/copyDistricts`,
            data,
            fetchOptions: {
                signal: abortSignal,
            },
        });
    },

    createPreset: function ({
        projectId,
        presetEntityName,
        data,
        abortSignal,
    }: {
        projectId: string;
        presetEntityName: string;
        data: FormDataObj;
        abortSignal: AbortSignal;
    }): Promise<
        | schema.definitions['entities_replancity_RoadInfrastructurePreset']
        | ApiError<SavePresetError>
    > {
        return restClient.fetch({
            method: 'POST',
            path: `presets/${projectId}/${presetEntityName}/upload`,
            data,
            fetchOptions: {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Content-Encoding': 'gzip',
                },
                signal: abortSignal,
            },
        });
    },

    undoLastOperation: function ({
        projectId,
        abortSignal,
    }: {
        projectId: string;
        abortSignal: AbortSignal;
    }): Promise<PublicTransitPresetEntity[]> {
        return restClient.fetch({
            method: 'GET',
            path: `services/replancity_RoadInfrastructureService/undoLastOperationNoPreset?projectId=${projectId}`,
            fetchOptions: { signal: abortSignal },
        });
    },

    copyPreset: function (args: CopyPresetFnArgs): Promise<PublicTransitPresetEntity | ApiError> {
        const { serviceName, data, abortSignal } = args;

        return restClient.fetch({
            method: 'GET',
            path: `services/${serviceName}/copyPreset`,
            data,
            fetchOptions: { signal: abortSignal },
        });
    },

    traceRoutesToNetwork: function (args: TraceRoutesToNetworkArgsType): Promise<any | ApiError> {
        const { serviceName, data, abortSignal } = args;

        return restClient.fetch({
            method: 'GET',
            path: `services/${serviceName}/traceRoutesToNetwork`,
            data,
            fetchOptions: { signal: abortSignal },
        });
    },

    traceLineToNetwork: function (args: TraceLineToNetworkArgsType): Promise<any | ApiError> {
        const { serviceName, data, abortSignal } = args;

        return restClient.fetch({
            method: 'GET',
            path: `services/${serviceName}/traceLineToNetwork`,
            data,
            fetchOptions: { signal: abortSignal },
        });
    },

    traceRouteToNetwork: function (args: TraceRouteToNetworkArgsType): Promise<any | ApiError> {
        const { serviceName, data, abortSignal } = args;

        return restClient.fetch({
            method: 'GET',
            path: `services/${serviceName}/traceRouteToNetwork`,
            data,
            fetchOptions: { signal: abortSignal },
        });
    },

    copyScenarioEvent: function (args: CopyScenarioEventArgsType): Promise<any | ApiError> {
        const { abortSignal, ...data } = args;

        return restClient.fetch({
            method: 'GET',
            path: `services/replancity_CaseScenarioService/copyEvent`,
            data,
            fetchOptions: { signal: abortSignal },
        });
    },

    importDistricts(data: FormData, abortSignal: AbortSignal): Promise<any[]> {
        return restClient.fetch({
            method: 'POST',
            path: `presets/upload_districts`,
            data,
            fetchOptions: {
                signal: abortSignal,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Content-Encoding': 'gzip',
                },
            },
        });
    },
};
